// Currency and number formatting utilities

/**
 * Formats a number as South African currency (ZAR)
 * @param amount - The amount to format
 * @param options - Additional formatting options
 * @returns Formatted currency string (e.g., "R 1,234.56")
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  options: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    prefix?: string;
    showSymbol?: boolean;
  } = {}
): string => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    prefix = 'R',
    showSymbol = true,
  } = options;

  // Convert to number and handle invalid values
  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return showSymbol ? `${prefix} 0.00` : '0.00';
  }

  const formatted = numAmount.toLocaleString('en-ZA', {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol ? `${prefix} ${formatted}` : formatted;
};

/**
 * Formats a number with thousand separators
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | string | null | undefined,
  decimals: number = 0
): string => {
  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '0';
  }

  return numValue.toLocaleString('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/** Strip grouping separators (comma / thin space / NBSP) for parsing. */
export function stripNumberGrouping(raw: string): string {
  return raw.replace(/[,\u00A0\u202F\s]/g, '');
}

/**
 * Parse a user-typed amount that may include thousand separators.
 * Accepts "1,234.56" or "1 234.56". Returns undefined for empty / incomplete input.
 */
export function parseGroupedNumber(raw: string | number | null | undefined): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;

  const trimmed = raw.trim();
  if (!trimmed || trimmed === '-' || trimmed === '.' || trimmed === '-.') return undefined;

  const normalized = stripNumberGrouping(trimmed);
  if (!/^-?\d*\.?\d*$/.test(normalized) || normalized === '' || normalized === '-' || normalized === '.') {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Format a number for amount inputs: thousand separators, optional fixed decimals.
 * Uses en-US so `.` stays the decimal separator while typing.
 */
export function formatGroupedNumber(
  value: number | string | null | undefined,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
): string {
  const num =
    typeof value === 'number' ? value : parseGroupedNumber(value ?? '');
  if (num === undefined || Number.isNaN(num)) return '';

  const { minimumFractionDigits = 0, maximumFractionDigits = 20 } = options;
  return num.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: true,
  });
}

/**
 * Live-format a partial amount string while the user types (keeps trailing ".").
 */
export function formatGroupedNumberInput(raw: string): string {
  if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return raw;

  const negative = raw.trimStart().startsWith('-');
  const cleaned = stripNumberGrouping(raw).replace(/[^\d.-]/g, '');
  const withoutExtraMinus = cleaned.replace(/(?!^)-/g, '');
  const body = withoutExtraMinus.replace(/^-/, '');
  const hasDot = body.includes('.');
  const [intRaw = '', ...fracParts] = body.split('.');
  const frac = fracParts.join('').replace(/\D/g, '');
  const intDigits = intRaw.replace(/\D/g, '');
  const intFormatted = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let result = (negative ? '-' : '') + intFormatted;
  if (hasDot) result += `.${frac}`;
  return result;
}

export function isPartialGroupedNumber(raw: string): boolean {
  const t = raw.trim();
  return t === '' || t === '-' || t === '.' || t === '-.' || /^-?\d{1,3}(,\d{3})*\.$/.test(t);
}

/**
 * Formats a percentage value
 * @param value - The percentage value (e.g., 0.25 for 25%)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | string | null | undefined,
  decimals: number = 1
): string => {
  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '0%';
  }

  return `${(numValue * 100).toFixed(decimals)}%`;
};

/**
 * Safely converts a value to a number for calculations
 * @param value - The value to convert
 * @param defaultValue - Default value if conversion fails (default: 0)
 * @returns Numeric value
 */
export const safeNumber = (
  value: number | string | null | undefined,
  defaultValue: number = 0
): number => {
  const numValue = Number(value);
  return isNaN(numValue) ? defaultValue : numValue;
};

/**
 * Formats file size in human readable format
 * @param bytes - Size in bytes
 * @returns Formatted size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Formats a date using specified format options
 * @param date - Date or date string to format
 * @param format - Format type
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string, format: 'short' | 'medium' | 'long' = 'medium'): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  
  const options: Record<string, Intl.DateTimeFormatOptions> = {
    short: { year: 'numeric', month: 'short', day: 'numeric' },
    medium: { year: 'numeric', month: 'long', day: 'numeric' },
    long: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  };
  
  return dateObj.toLocaleDateString('en-ZA', options[format]);
};

/**
 * Converts an ISO datetime string to a date-only string for HTML date inputs
 * @param date - ISO datetime string or Date object (e.g., "2025-05-27T13:01:07.205Z" or Date object)
 * @returns Date string in YYYY-MM-DD format (e.g., "2025-05-27")
 */
export function formatDateForInput(date: string | Date | undefined | null): string {
  if (!date) {
    return '';
  }
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    // Get YYYY-MM-DD format
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return '';
  }
}

/**
 * Converts a date-only string to an ISO date string for API submission
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO date string (e.g., "2025-05-27T00:00:00.000Z")
 */
export function formatDateForAPI(dateString: string): string {
  if (!dateString) {
    return '';
  }
  
  try {
    // Ensure we're working with just the date part
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) {
      return '';
    }
    
    // Create date at midnight UTC
    const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
    return date.toISOString();
  } catch (error) {
    return '';
  }
} 